/**
 * `DexieStorage` — the web/IndexedDB implementation of `IStorage`.
 *
 * CP2: consumers never import this module directly in production code; they
 * receive an `IStorage` via an adapter wired at app entry. The contract test
 * suite, the legacy-seed test, and the app-entry wiring are the only
 * legitimate imports.
 */

import type { UpdateSpec } from 'dexie';
import type { HouseholdProduct, ShoppingList, ShoppingListItem } from '@shopista/shared/types';
import type {
  IStorage,
  PendingEvent,
  ProductSuggestion,
  PurchaseCadence,
  StoreLocation_RetailerOffer_AislePosition,
  SyncState,
} from '../interface/index.js';
import { ShoppingDatabase } from './database.js';

export class DexieStorage implements IStorage {
  constructor(private readonly db: ShoppingDatabase) {}

  // -------------------------------------------------------------------------
  // ShoppingList
  // -------------------------------------------------------------------------

  async getList(id: string): Promise<ShoppingList | undefined> {
    return this.db.shoppingLists.get(id);
  }

  async listLists(): Promise<ShoppingList[]> {
    return this.db.shoppingLists.toArray();
  }

  async putList(list: ShoppingList): Promise<void> {
    await this.db.shoppingLists.put(list);
  }

  async deleteList(id: string): Promise<void> {
    await this.db.shoppingLists.delete(id);
  }

  async clearLists(): Promise<void> {
    await this.db.shoppingLists.clear();
  }

  // -------------------------------------------------------------------------
  // ShoppingListItem
  // -------------------------------------------------------------------------

  async getItem(id: string): Promise<ShoppingListItem | undefined> {
    return this.db.cachedListItems.get(id);
  }

  async listItemsByList(shoppinglistId: string): Promise<ShoppingListItem[]> {
    return this.db.cachedListItems.where('shoppinglist_id').equals(shoppinglistId).toArray();
  }

  async listItemsByProduct(householdproductId: string): Promise<ShoppingListItem[]> {
    return this.db.cachedListItems
      .where('householdproduct_id')
      .equals(householdproductId)
      .toArray();
  }

  async putItem(item: ShoppingListItem): Promise<void> {
    await this.db.cachedListItems.put(item);
  }

  async putItems(items: ShoppingListItem[]): Promise<void> {
    if (items.length === 0) return;
    await this.db.cachedListItems.bulkPut(items);
  }

  async updateItem(id: string, patch: Partial<ShoppingListItem>): Promise<void> {
    // Dexie's `update` narrows `UpdateSpec<T>` over per-property keypath
    // unions. `Partial<T>` is a legal subset but requires an explicit cast.
    await this.db.cachedListItems.update(id, patch as UpdateSpec<ShoppingListItem>);
  }

  async deleteItem(id: string): Promise<void> {
    await this.db.cachedListItems.delete(id);
  }

  async clearItems(): Promise<void> {
    await this.db.cachedListItems.clear();
  }

  // -------------------------------------------------------------------------
  // HouseholdProduct
  // -------------------------------------------------------------------------

  async getProduct(id: string): Promise<HouseholdProduct | undefined> {
    return this.db.cachedProducts.get(id);
  }

  async listProducts(): Promise<HouseholdProduct[]> {
    return this.db.cachedProducts.toArray();
  }

  async putProduct(product: HouseholdProduct): Promise<void> {
    await this.db.cachedProducts.put(product);
  }

  async putProducts(products: HouseholdProduct[]): Promise<void> {
    if (products.length === 0) return;
    await this.db.cachedProducts.bulkPut(products);
  }

  async updateProduct(id: string, patch: Partial<HouseholdProduct>): Promise<void> {
    await this.db.cachedProducts.update(id, patch as UpdateSpec<HouseholdProduct>);
  }

  async deleteProduct(id: string): Promise<void> {
    await this.db.cachedProducts.delete(id);
  }

  async clearProducts(): Promise<void> {
    await this.db.cachedProducts.clear();
  }

  // -------------------------------------------------------------------------
  // PendingEvent
  // -------------------------------------------------------------------------

  async addEvent(event: PendingEvent): Promise<void> {
    await this.db.pendingEventsV2.add(event);
  }

  async getEvent(id: string): Promise<PendingEvent | undefined> {
    return this.db.pendingEventsV2.get(id);
  }

  async listAllEvents(): Promise<PendingEvent[]> {
    return this.db.pendingEventsV2.toArray();
  }

  async listUnsyncedEvents(): Promise<PendingEvent[]> {
    // Legacy note: IndexedDB boolean indexing is unreliable, so we always
    // filter in-memory. Preserved for behavioural parity with legacy.
    const all = await this.db.pendingEventsV2.toArray();
    return all.filter((e) => e.synced === false);
  }

  async markEventsSynced(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.db.pendingEventsV2.update(id, { synced: true });
    }
  }

  async deleteSyncedEvents(): Promise<void> {
    const all = await this.db.pendingEventsV2.toArray();
    const syncedIds = all.filter((e) => e.synced === true).map((e) => e.id);
    if (syncedIds.length > 0) {
      await this.db.pendingEventsV2.bulkDelete(syncedIds);
    }
  }

  async countUnsyncedEvents(): Promise<number> {
    const all = await this.db.pendingEventsV2.toArray();
    return all.filter((e) => e.synced === false).length;
  }

  async clearEvents(): Promise<void> {
    await this.db.pendingEventsV2.clear();
  }

  // -------------------------------------------------------------------------
  // SyncState
  // -------------------------------------------------------------------------

  async getSyncState(deviceId: string): Promise<SyncState | undefined> {
    return this.db.syncStateV2.get(deviceId);
  }

  async putSyncState(state: SyncState): Promise<void> {
    await this.db.syncStateV2.put(state);
  }

  async clearSyncState(): Promise<void> {
    await this.db.syncStateV2.clear();
  }

  async isHouseholdSeeded(householdId: string): Promise<boolean> {
    return (await this.db.syncStateV2.get(`seed:${householdId}`)) !== undefined;
  }

  async markHouseholdSeeded(householdId: string): Promise<void> {
    await this.db.syncStateV2.put({
      deviceId: `seed:${householdId}`,
      lastSyncTimestamp: 'seeded',
      lastSyncAt: Date.now(),
    });
  }

  // -------------------------------------------------------------------------
  // PurchaseCadence
  // -------------------------------------------------------------------------

  async getPurchaseCadence(householdproductId: string): Promise<PurchaseCadence | undefined> {
    return this.db.purchaseCadences.where('householdproduct_id').equals(householdproductId).first();
  }

  async listPurchaseCadences(): Promise<PurchaseCadence[]> {
    return this.db.purchaseCadences.toArray();
  }

  async putPurchaseCadence(cadence: PurchaseCadence): Promise<void> {
    await this.db.purchaseCadences.put(cadence);
  }

  async deletePurchaseCadence(householdproductId: string): Promise<void> {
    await this.db.purchaseCadences.where('householdproduct_id').equals(householdproductId).delete();
  }

  async clearPurchaseCadences(): Promise<void> {
    await this.db.purchaseCadences.clear();
  }

  // -------------------------------------------------------------------------
  // ProductSuggestion
  // -------------------------------------------------------------------------

  async getProductSuggestion(householdproductId: string): Promise<ProductSuggestion | undefined> {
    return this.db.productSuggestions
      .where('householdproduct_id')
      .equals(householdproductId)
      .first();
  }

  async listProductSuggestions(): Promise<ProductSuggestion[]> {
    return this.db.productSuggestions.toArray();
  }

  async putProductSuggestion(suggestion: ProductSuggestion): Promise<void> {
    await this.db.productSuggestions.put(suggestion);
  }

  async putProductSuggestions(suggestions: ProductSuggestion[]): Promise<void> {
    if (suggestions.length === 0) return;
    await this.db.productSuggestions.bulkPut(suggestions);
  }

  async deleteProductSuggestion(householdproductId: string): Promise<void> {
    await this.db.productSuggestions
      .where('householdproduct_id')
      .equals(householdproductId)
      .delete();
  }

  async clearProductSuggestions(): Promise<void> {
    await this.db.productSuggestions.clear();
  }

  // -------------------------------------------------------------------------
  // AislePosition (T12.0.18.2)
  // -------------------------------------------------------------------------

  async getAislePosition(
    storelocationId: string,
    retailerofferId: string,
  ): Promise<StoreLocation_RetailerOffer_AislePosition | undefined> {
    const matches = await this.db.aislePositions
      .where('[storelocation_id+retaileroffer_id]')
      .equals([storelocationId, retailerofferId])
      .toArray();
    return matches[0];
  }

  async listAislePositions(
    storelocationId: string,
  ): Promise<StoreLocation_RetailerOffer_AislePosition[]> {
    return this.db.aislePositions.where('storelocation_id').equals(storelocationId).toArray();
  }

  async putAislePosition(entry: StoreLocation_RetailerOffer_AislePosition): Promise<void> {
    await this.db.aislePositions.put(entry);
  }

  async deleteAislePosition(storelocationId: string, retailerofferId: string): Promise<void> {
    const existing = await this.getAislePosition(storelocationId, retailerofferId);
    if (existing) {
      await this.db.aislePositions.delete(existing.id);
    }
  }

  async clearAislePositions(): Promise<void> {
    await this.db.aislePositions.clear();
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  async clearDomainData(): Promise<void> {
    // Mirrors legacy `clearV2Data()` — wipes domain data but preserves
    // pending events so unsynced offline changes are not lost. Also
    // preserves `seed:<householdId>` rows in syncStateV2 (BUG69) — a full
    // resync clears device cursors, but must not un-seed itself: the
    // marker is only meant to be cleared by `clearSyncState()`.
    await Promise.all([
      this.db.cachedListItems.clear(),
      this.db.cachedProducts.clear(),
      this.db.shoppingLists.clear(),
      this.db.syncStateV2.filter((s) => !s.deviceId.startsWith('seed:')).delete(),
      this.db.purchaseCadences.clear(),
      this.db.productSuggestions.clear(),
      this.db.aislePositions.clear(),
    ]);
  }

  async close(): Promise<void> {
    this.db.close();
  }
}

/**
 * Factory — default entry point for wiring the web impl at app entry.
 *
 * @param name optional IndexedDB database name. Default matches legacy
 *   (`ShoppingCoordinationDB`). Tests pass a unique name per case to isolate.
 */
export function createDexieStorage(name?: string): IStorage {
  const db = new ShoppingDatabase(name);
  return new DexieStorage(db);
}
